var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { UnsafeProposedOrPreviousValueOf } from "../../../../ChronoGraph/chrono/Effect.js";
import { Mixin } from '../../../../ChronoGraph/class/BetterMixin.js';
import { prototypeValue } from '../../../../ChronoGraph/util/Helpers.js';
import DateHelper from '../../../../Core/helper/DateHelper.js';
import Localizable from '../../../../Core/localization/Localizable.js';
import { ConflictResolution, ConstraintInterval, ConstraintIntervalDescription } from '../../../chrono/Conflict.js';
import { model_field } from '../../../chrono/ModelFieldAtom.js';
import "../../../localization/En.js";
import { ConstraintType } from "../../../scheduling/Types.js";
import { format } from '../../../util/Functions.js';
import { HasChildrenMixin } from "../scheduler_basic/HasChildrenMixin.js";
import { HasDependenciesMixin } from "../scheduler_basic/HasDependenciesMixin.js";
import { ConstrainedEarlyEventMixin } from './ConstrainedEarlyEventMixin.js';
import { HasDateConstraintMixin } from "./HasDateConstraintMixin.js";
import { ConstrainedByDependenciesEventScheduleMixin } from "./ConstrainedByDependenciesEventScheduleMixin.js";
//---------------------------------------------------------------------------------------------------------------------
/**
 * This mixin adds support for scheduling event ASAP, by dependencies. All it does is
 * create the constraint interval for every incoming dependency. See [[ConstrainedEarlyEventMixin]] for
 * more details about constraint-based scheduling.
 *
 * The supported dependency types are listed in this enum: [[DependencyType]]
 */
export class ConstrainedByDependenciesEventMixin extends Mixin(
// This mixin's `calculateEffectiveDirection` method should override the one of the `HasChildrenMixin`
// thus the dependency on it (not strictly needed)
[ConstrainedEarlyEventMixin, HasDependenciesMixin, HasChildrenMixin, HasDateConstraintMixin], (base) => {
    class ConstrainedByDependenciesEventMixin extends base {
        constructor() {
            super(...arguments);
            this.isInitialConstraintCalculation = 0b11;
        }
        /**
         * The method is used during forward (from start to end) scheduling. It defines whether the provided dependency
         * should constrain the successor or not. If the method returns `true` the dependency constrains the successor and does not do that when `false` returned.
         * By default the method returns `true` if the dependency is [[SchedulerProDependencyMixin.active|active]]
         * and if this event is [[inactive|active]] (or both this event and the successor are [[inactive]]).
         *
         * @param dependency Dependency to consider.
         * @returns `true` if the dependency should constrain successor, `false` if not.
         */
        *shouldPredecessorAffectScheduling(dependency) {
            return true;
        }
        /**
         * The method is used during backward (from end to start) scheduling. It defines whether the provided dependency
         * should constrain the predecessor or not. If the method returns `true` the dependency constrains the predecessor and does not do that when `false` returned.
         * By default the method returns `true` if the dependency is [[SchedulerProDependencyMixin.active|active]]
         * and if this event is [[inactive|active]] (or both this event and the predecessor are [[inactive]]).
         *
         * @param dependency Dependency to consider.
         * @returns `true` if the dependency should constrain successor, `false` if not.
         */
        *shouldSuccessorAffectScheduling(dependency) {
            return true;
        }
        *shouldAutoSetConstraintOnLoad() {
            return this.project.autoSetConstraints
                && (yield this.$.incomingDeps).size === 0
                && (yield this.$.childEvents).size === 0
                && !(yield this.$.manuallyScheduled);
        }
        *calculateConstraintType() {
            let constraintType;
            // if `autoSetConstraints` is enabled, we set a constraintType to pin tasks when data is being loaded
            if (this.isInitialConstraintCalculation & 0b01) {
                constraintType = yield UnsafeProposedOrPreviousValueOf(this.$.constraintType);
                if (constraintType == null && (yield* this.shouldAutoSetConstraintOnLoad())) {
                    constraintType = ConstraintType.StartNoEarlierThan;
                }
                else {
                    constraintType = yield* super.calculateConstraintType();
                }
            }
            else {
                constraintType = yield* super.calculateConstraintType();
            }
            // automatic constraint setting should happen only during initial load
            this.isInitialConstraintCalculation &= 0b10;
            return constraintType;
        }
        *calculateConstraintDate(Y) {
            let constraintDate;
            if (this.isInitialConstraintCalculation & 0b10) {
                constraintDate = yield UnsafeProposedOrPreviousValueOf(this.$.constraintDate);
                // If this is a new task, and autoSetConstraints is enabled, we set a constraintDate to pin tasks when
                // data is being loaded
                if (constraintDate == null && (yield* this.shouldAutoSetConstraintOnLoad())) {
                    constraintDate = yield UnsafeProposedOrPreviousValueOf(this.$.startDate);
                }
                else {
                    constraintDate = yield* super.calculateConstraintDate(Y);
                }
            }
            else {
                constraintDate = yield* super.calculateConstraintDate(Y);
            }
            // automatic constraint setting should happen only during initial load
            this.isInitialConstraintCalculation &= 0b01;
            return constraintDate;
        }
    }
    // @ts-ignore
    ConstrainedByDependenciesEventMixin.scheduleMixinClass = Mixin([base.scheduleMixinClass, ConstrainedByDependenciesEventScheduleMixin], base => base);
    __decorate([
        model_field({ type: 'boolean' })
    ], ConstrainedByDependenciesEventMixin.prototype, "inactive", void 0);
    return ConstrainedByDependenciesEventMixin;
}) {
}
/**
 * Base class for dependency interval resolutions.
 */
export class BaseDependencyResolution extends Localizable(ConflictResolution) {
    static get $name() {
        return 'BaseDependencyResolution';
    }
    getDescription() {
        const { dependency } = this, { type, fromEvent, toEvent } = dependency;
        return format(this.L('L{descriptionTpl}'), this.L('L{DependencyType.long}')[type], fromEvent.name || fromEvent.id, toEvent.name || toEvent.id);
    }
}
/**
 * Dependency resolution removing the dependency.
 */
export class RemoveDependencyResolution extends BaseDependencyResolution {
    static get $name() {
        return 'RemoveDependencyResolution';
    }
    /**
     * Resolves the conflict by removing the dependency.
     */
    resolve() {
        this.dependency.remove();
    }
}
/**
 * Dependency resolution deactivating the dependency.
 */
export class DeactivateDependencyResolution extends BaseDependencyResolution {
    static get $name() {
        return 'DeactivateDependencyResolution';
    }
    /**
     * Resolves the conflict by deactivating the dependency.
     */
    resolve() {
        this.dependency.active = false;
    }
}
/**
 * Description builder for a [[DependencyConstraintInterval|dependency constraint interval]].
 */
export class DependencyConstraintIntervalDescription extends ConstraintIntervalDescription {
    static get $name() {
        return 'DependencyConstraintIntervalDescription';
    }
    static getDescriptionParameters(interval) {
        const dependency = interval.owner;
        return [
            DateHelper.format(interval.startDate, this.L('L{dateFormat}')),
            DateHelper.format(interval.endDate, this.L('L{dateFormat}')),
            this.L('L{DependencyType.long}')[dependency.type],
            dependency.fromEvent.name,
            dependency.toEvent.name
        ];
    }
}
/**
 * Constraint interval applied by a dependency.
 *
 * In case for a conflict the class [[getResolutions|suggests]] two resolution options:
 * either [[RemoveDependencyResolution|removing]] or [[DeactivateDependencyResolution|deactivating]] the dependency.
 */
export class DependencyConstraintInterval extends ConstraintInterval {
    get isDependencyConstraintInterval() {
        return true;
    }
    isAffectedByTransaction(transaction) {
        const dependency = this.owner;
        transaction = transaction || dependency.graph.activeTransaction;
        const { entries } = transaction, 
        // dependency identifiers to check
        { fromEvent, toEvent, lag, lagUnit, type } = dependency.$, fromEventQuark = entries.get(fromEvent), toEventQuark = entries.get(toEvent), lagQuark = entries.get(lag), lagUnitQuark = entries.get(lagUnit), typeQuark = entries.get(type);
        // new or modified dependency
        return !transaction.baseRevision.hasIdentifier(dependency.$$) ||
            fromEventQuark && !fromEventQuark.isShadow() ||
            toEventQuark && !toEventQuark.isShadow() ||
            lagQuark && !lagQuark.isShadow() ||
            lagUnitQuark && !lagUnitQuark.isShadow() ||
            typeQuark && !typeQuark.isShadow();
    }
    /**
     * Returns the interval resolution options.
     * There are two resolutions:
     * - [[RemoveDependencyResolution|removing the dependency]]
     * - [[DeactivateDependencyResolution|deactivating the dependency]].
     */
    getResolutions() {
        return this.resolutions || (this.resolutions = [
            this.deactivateDependencyConflictResolutionClass.new({ dependency: this.owner }),
            this.removeDependencyConflictResolutionClass.new({ dependency: this.owner })
        ]);
    }
}
__decorate([
    prototypeValue(RemoveDependencyResolution)
], DependencyConstraintInterval.prototype, "removeDependencyConflictResolutionClass", void 0);
__decorate([
    prototypeValue(DeactivateDependencyResolution)
], DependencyConstraintInterval.prototype, "deactivateDependencyConflictResolutionClass", void 0);
__decorate([
    prototypeValue(DependencyConstraintIntervalDescription)
], DependencyConstraintInterval.prototype, "descriptionBuilderClass", void 0);
